import { useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Lock, ArrowRight } from "lucide-react";
import { LogoMark } from "@/components/Logo";

export default function PasswordGate({ onSuccess }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/session/start", {
        code: pw,
        user_agent: navigator.userAgent,
      });
      toast.success(data.partner_name ? `Welcome, ${data.partner_name}` : "Access granted");
      onSuccess(data);
    } catch {
      toast.error("Invalid access code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-background">
      <div className="absolute inset-0 grain" />
      <div
        className="absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage:
            "linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-8 py-6 text-[11px] uppercase tracking-brand">
        <div className="flex items-center gap-3">
          <LogoMark className="h-7 w-auto" />
        </div>
        <span className="text-muted-foreground font-mono-data">CONFIDENTIAL · PARTNER PREVIEW</span>
      </div>

      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-8 py-6 text-[11px] uppercase tracking-brand text-muted-foreground">
        <span className="font-mono-data">v. 2026.02</span>
        <span>Do not distribute</span>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-xl mx-auto px-6"
        data-testid="password-gate"
      >
        <div className="bg-white border border-foreground">
          <div className="px-10 pt-10 pb-8 border-b border-foreground">
            <div className="flex items-center gap-3 mb-10">
              <Lock className="w-4 h-4" strokeWidth={1.5} />
              <span className="overline">Partner Access</span>
            </div>
            <h1 className="font-display text-5xl md:text-6xl tracking-tighter leading-[0.95] font-medium">
              Modern<br />
              <em className="italic text-primary">Servicing</em><br />
              Infrastructure.
            </h1>
            <p className="mt-6 text-base text-muted-foreground max-w-md leading-relaxed">
              A private walkthrough of how IBA Servicing powers everything that
              happens after a loan is funded — and what it saves you.
            </p>
          </div>

          <form onSubmit={submit} className="px-10 py-8">
            <label htmlFor="deck-pw" className="overline block mb-3">
              Access Code
            </label>
            <div className="flex items-end gap-4">
              <input
                id="deck-pw"
                data-testid="password-input"
                type="password"
                autoFocus
                autoComplete="off"
                placeholder="Enter your partner code"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="input-sharp flex-1"
              />
              <button
                type="submit"
                data-testid="password-submit"
                disabled={loading}
                className="btn-primary disabled:opacity-50"
              >
                {loading ? "Verifying…" : "Enter"}
                <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
            <p className="mt-6 text-xs text-muted-foreground font-mono-data">
              Each partner receives a unique access code. Contact your IBA Servicing representative if you need one.
            </p>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
